import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import App from '@/App';
import { RouteGuard } from '@features/auth/shared/components';
import { AppShell } from '@shared/layout';
import { LoadingSkeleton, RouteErrorBoundary } from '@shared/components';
import { useAuth } from '@shared/hooks/useAuth';
import {
  getLocaleFromPathname,
  localizePath,
  resolvePreferredLocale,
  useLocale,
} from '@shared/i18n';
import { localizeRoutes } from './routeLocalization';
import { isAftercareEnabled } from '@shared/lib/featureFlags';

// Main dashboard component that handles role-based routing
const DashboardRouter = lazy(() =>
  import('@shared/components/dashboard/DashboardRouter').then(m => ({ default: m.DashboardRouter }))
);
const MessagesPage = lazy(() => import('@features/messages'));
const CallsPage = lazy(() => import('@shared/calls/CallsPage'));

const PaymentComplete = lazy(() => import("@features/patient/pages/payments/PaymentComplete"));

// Auth pages
const SignIn = lazy(() => import('@features/auth/pages/sign-in'));
const PatientSignIn = lazy(() => import('@features/auth/pages/patient-sign-in'));
const StaffSignIn = lazy(() => import('@features/auth/pages/jema-partner-sign-in'));
const ForgotPassword = lazy(() => import('@features/auth/pages/forgot-password'));
const ResetPassword = lazy(() => import('@features/auth/pages/reset-password'));
const MagicLinkCallback = lazy(() => import('@features/auth/pages/magic-link-callback'));
const MagicLinkRequired = lazy(() => import('@features/auth/pages/magic-link-required'));
const AuthError = lazy(() => import('@features/auth/pages/auth-error'));
const SetupPassword = lazy(() => import('@features/auth/pages/setup-password'));

// Patient docs portal (token + OTP, no login)
const DocsPortalOtpPage = lazy(() => import('@features/shared/pages/docs-portal/OtpPage'));
const DocsPortalUploadPage = lazy(() => import('@features/shared/pages/docs-portal/UploadPage'));
const FlightsPortalOtpPage = lazy(() => import('@features/shared/pages/flights-portal/OtpPage'));
const FlightsPortalFormPage = lazy(() => import('@features/shared/pages/flights-portal/FormPage'));
const UserSettingsPage = lazy(() => import('@features/shared/pages/settings'));

// Patient pages
const PatientQuotesPage = lazy(() => import('@features/patient/pages/quotes'));
const PatientTravelPage = lazy(() => import('@features/patient/pages/travel/Page'));
const PatientPaymentsPage = lazy(() => import('@features/patient/pages/payments'));
const PatientTreatmentPage = lazy(() => import('@features/patient/pages/treatment'));
const PatientAftercareCheckInPage = lazy(() => import('@features/patient/pages/aftercare/checkin'));
const PatientAftercareIssuePage = lazy(() => import('@features/patient/pages/aftercare/issue'));

// Doctor pages
const DoctorMyDayPage = lazy(() => import('@features/doctor/pages/my-day'));
const DoctorPatientsPage = lazy(() => import('@features/doctor/pages/patients'));
const DoctorQuotesPage = lazy(() => import('@features/doctors/quotes'));
const DoctorCalendarPage = lazy(() => import('@features/doctor/pages/calendar'));
const DoctorPaymentsPage = lazy(() => import('@features/doctor/pages/payments'));
const DoctorInvitationAcceptPage = lazy(() => import('@features/doctor/pages/invitation-accept'));
const DoctorOrganizationManagePage = lazy(
  () => import('@features/doctor/pages/organization-manage')
);
const DoctorProfilePage = lazy(() => import('@features/doctor/pages/profile'));
const DoctorQuickOnboardingPage = lazy(() => import('@features/doctor/pages/quick-onboarding'));
const DoctorDiditCallbackPage = lazy(() => import('@features/doctor/pages/didit-callback/Page'));
const DoctorOnboarding = lazy(() => import('@features/doctor/onboarding'));
const DoctorAccountControlApprovalPage = lazy(
  () => import('@features/shared/pages/doctor-account-control/ApprovalPage')
);
const OwnershipTransferAcceptPage = lazy(
  () => import('@features/shared/pages/ownership-transfer-accept')
);
const DoctorPublicProfilePage = lazy(() => import('@features/doctor/pages/public-profile'));

// Admin pages
const AdminPatientsPage = lazy(() => import('@features/admin/pages/patients'));
const AdminQuotingQueuePage = lazy(() => import('@features/admin/pages/quoting-queue'));
const AdminLogisticsPage = lazy(() => import('@features/admin/pages/logistics'));
const AdminAftercarePage = lazy(() => import('@features/admin/pages/aftercare'));
const AdminSalesPage = lazy(() => import('@features/admin/pages/sales'));
const AdminPaymentsPage = lazy(() => import('@features/admin/pages/payments'));
const AdminReportsPage = lazy(() => import('@features/admin/pages/reports'));
const AdminUserManagementPage = lazy(() => import('@features/admin/pages/user-management'));
const AdminServiceRequestsPage = lazy(() => import('@features/admin/pages/service-requests'));
const AdminPricingCalculatorPage = lazy(() => import('@features/admin/pages/pricing-calculator'));
const AdminDoctorAccountControlWorkspacePage = lazy(
  () => import('@features/admin/pages/doctor-account-control/Page')
);
const AdminTravelPlanningPage = lazy(
  () => import('@features/quoting-queue/pages/travel-planning/TravelPlanningPage')
);
const AdminSpecialtyProcedureCatalogPage = lazy(
  () => import('@features/admin/pages/specialty-procedure-catalog')
);
const AdminVoiceOutreachPage = lazy(() => import('@features/admin/pages/voice-outreach'));

// Super Admin pages
const SuperAdminUserManagement = lazy(() => import('@features/super-admin/pages/user-management'));
const SuperAdminOrganizationManagement = lazy(
  () => import('@features/super-admin/pages/organization-management')
);
const SuperAdminOrganizationPlans = lazy(
  () => import('@features/super-admin/pages/organization-plans')
);
const SuperAdminKanban = lazy(() => import('@features/super-admin/pages/super-admin-kanban'));
const SuperAdminLogistics = lazy(() => import('@features/super-admin/pages/super-admin-logistics'));
const SuperAdminBillingManagement = lazy(
  () => import('@features/super-admin/pages/billing-management')
);
const SuperAdminPaymentManagement = lazy(
  () => import('@features/super-admin/pages/payment-management')
);
const SuperAdminAuditLogs = lazy(() => import('@features/super-admin/pages/audit-logs'));
const SuperAdminNotifications = lazy(() => import('@features/super-admin/pages/notifications'));
const SuperAdminReportsAnalytics = lazy(
  () => import('@features/super-admin/pages/reports-analytics')
);
const SuperAdminDashboardSettings = lazy(
  () => import('@features/super-admin/pages/dashboard-settings')
);
const SuperAdminVerifications = lazy(() => import('@features/super-admin/pages/verifications'));
const SuperAdminAiSales = lazy(() => import('@features/super-admin/pages/ai-sales'));

// Role-based component routers
const QuotesRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'doctor' || user?.role === 'admin') return <DoctorQuotesPage />;
  return <PatientQuotesPage />;
};

const PaymentsRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'admin') return <AdminPaymentsPage />;
  if (user?.role === 'doctor') return <DoctorPaymentsPage />;
  return <PatientPaymentsPage />;
};

const CalendarRouter = () => {
  return <DoctorCalendarPage />;
};

const PatientsRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'super_admin') return <AdminPatientsPage />;
  return <DoctorPatientsPage />;
};

const NavigateToDoctorProfile = () => {
  const { slug } = useParams();
  return <Navigate to={`/doctors/${slug}`} replace />;
};

const RedirectToDefaultLocale = () => {
  const location = useLocation();
  const { locale } = useLocale();
  const currentUrl = `${location.pathname}${location.search}${location.hash}`;
  const destination = localizePath(
    currentUrl,
    getLocaleFromPathname(location.pathname) ?? locale ?? resolvePreferredLocale(location.pathname)
  );
  if (destination === currentUrl) return <RouteErrorBoundary />;
  return <Navigate to={destination} replace />;
};

// Children of the persistent AppShell layout route.
// AppShell mounts once; Outlet swaps only the page content on navigation.
//
// No per-route <Suspense> here — suspensions from lazy components propagate
// up to AppShell's own persistent <Suspense> boundary. Because that boundary
// already has committed content, React 18's startTransition behaviour keeps
// the OLD page visible until the new chunk is ready, producing zero flicker.
export const shellChildren = [
  { path: '/dashboard', element: <DashboardRouter /> },
  { path: '/settings', element: <UserSettingsPage /> },
  { path: '/quotes', element: <QuotesRouter /> },
  { path: '/payments', element: <PaymentsRouter /> },
  {
    path: '/patients',
    element: (
      <RouteGuard requiredRoles={['doctor', 'super_admin']}>
        <PatientsRouter />
      </RouteGuard>
    ),
  },
  {
    path: '/calendar',
    element: (
      <RouteGuard requiredRoles={['doctor', 'admin', 'super_admin']}>
        <CalendarRouter />
      </RouteGuard>
    ),
  },
  {
    path: '/travel',
    element: (
      <RouteGuard requiredRoles={['patient']}>
        <PatientTravelPage />
      </RouteGuard>
    ),
  },
  {
    path: '/treatment',
    element: (
      <RouteGuard requiredRoles={['patient']}>
        <PatientTreatmentPage />
      </RouteGuard>
    ),
  },
  {
    path: '/my-day',
    element: (
      <RouteGuard requiredRoles={['doctor', 'admin']}>
        <DoctorMyDayPage />
      </RouteGuard>
    ),
  },
  { path: '/messages', element: <MessagesPage /> },
  {
    path: '/calls',
    element: (
      <RouteGuard requiredRoles={['admin', 'super_admin']}>
        <CallsPage />
      </RouteGuard>
    ),
  },
  {
    path: '/organization/manage',
    element: (
      <RouteGuard requiredRoles={['doctor']}>
        <DoctorOrganizationManagePage />
      </RouteGuard>
    ),
  },
  {
    path: '/doctor-profile',
    element: (
      <RouteGuard requiredRoles={['doctor']}>
        <DoctorProfilePage />
      </RouteGuard>
    ),
  },
  {
    path: '/quoting-queue',
    element: (
      <RouteGuard requiredRoles={['admin']}>
        <AdminQuotingQueuePage />
      </RouteGuard>
    ),
  },
  {
    path: '/logistics',
    element: (
      <RouteGuard requiredRoles={['admin']}>
        <AdminLogisticsPage />
      </RouteGuard>
    ),
  },
  {
    path: '/sales',
    element: (
      <RouteGuard requiredRoles={['admin', 'super_admin']}>
        <AdminSalesPage />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/sales',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <AdminSalesPage />
      </RouteGuard>
    ),
  },
  {
    path: '/reports',
    element: (
      <RouteGuard requiredRoles={['admin']}>
        <AdminReportsPage />
      </RouteGuard>
    ),
  },
  {
    path: '/admin/users',
    element: (
      <RouteGuard requiredRoles={['admin', 'super_admin']}>
        <AdminUserManagementPage />
      </RouteGuard>
    ),
  },
  {
    path: '/admin/service-requests',
    element: (
      <RouteGuard requiredRoles={['admin', 'super_admin']}>
        <AdminServiceRequestsPage />
      </RouteGuard>
    ),
  },
  {
    path: '/admin/pricing-calculator',
    element: (
      <RouteGuard requiredRoles={['admin']}>
        <AdminPricingCalculatorPage />
      </RouteGuard>
    ),
  },
  {
    path: '/admin/doctor-control/*',
    element: (
      <RouteGuard requiredRoles={['doctor', 'admin', 'super_admin']}>
        <AdminDoctorAccountControlWorkspacePage />
      </RouteGuard>
    ),
  },
  {
    path: '/admin/travel-planning/:travelCaseId',
    element: (
      <RouteGuard requiredRoles={['admin', 'super_admin']}>
        <AdminTravelPlanningPage />
      </RouteGuard>
    ),
  },
  {
    path: '/admin/procedure-catalog',
    element: (
      <RouteGuard requiredRoles={['admin', 'super_admin']}>
        <AdminSpecialtyProcedureCatalogPage />
      </RouteGuard>
    ),
  },
  {
    path: '/admin/voice-outreach',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <AdminVoiceOutreachPage />
      </RouteGuard>
    ),
  },
  {
    // Admins work the callback queue; only super admins can build campaigns,
    // which the page enforces by hiding the campaign sections rather than the
    // whole surface. The API draws the same line independently.
    path: '/super-admin/ai-sales',
    element: (
      <RouteGuard requiredRoles={['admin', 'super_admin']}>
        <SuperAdminAiSales />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/users',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminUserManagement />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/organizations',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminOrganizationManagement />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/verifications',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminVerifications />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/plans',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminOrganizationPlans />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/pricing-calculator',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <AdminPricingCalculatorPage />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/procedure-catalog',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <AdminSpecialtyProcedureCatalogPage />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/kanban',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminKanban />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/logistics',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminLogistics />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/billing',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminBillingManagement />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/payments',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminPaymentManagement />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/audit',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminAuditLogs />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/notifications',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminNotifications />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/reports',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminReportsAnalytics />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/settings',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminDashboardSettings />
      </RouteGuard>
    ),
  },
  // Care Journey PR4: only routed with the flag on (docs/care-journey/progress/PR4.md).
  ...(isAftercareEnabled()
    ? [
        {
          path: '/aftercare',
          element: (
            <RouteGuard requiredRoles={['admin']}>
              <AdminAftercarePage />
            </RouteGuard>
          ),
        },
        {
          path: '/treatment/:serviceRequestId/check-in/:taskId',
          element: (
            <RouteGuard requiredRoles={['patient']}>
              <PatientAftercareCheckInPage />
            </RouteGuard>
          ),
        },
        {
          path: '/treatment/:serviceRequestId/issues/:issueId',
          element: (
            <RouteGuard requiredRoles={['patient']}>
              <PatientAftercareIssuePage />
            </RouteGuard>
          ),
        },
        {
          path: '/super-admin/aftercare',
          element: (
            <RouteGuard requiredRoles={['super_admin']}>
              <AdminAftercarePage />
            </RouteGuard>
          ),
        },
      ]
    : []),
];

const baseRoutes = [
  { path: "/payment-complete", element: <Suspense fallback={<LoadingSkeleton />}><PaymentComplete /></Suspense> },
  {
    path: '/',
    element: <App />,
    errorElement: <RouteErrorBoundary />,
  },

  // Public portal routes (token-gated, no auth required)
  {
    path: '/docs/:token',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DocsPortalOtpPage />
      </Suspense>
    ),
  },
  {
    path: '/docs/:token/upload',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DocsPortalUploadPage />
      </Suspense>
    ),
  },
  {
    path: '/flights/:token',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <FlightsPortalOtpPage />
      </Suspense>
    ),
  },
  {
    path: '/flights/:token/form',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <FlightsPortalFormPage />
      </Suspense>
    ),
  },

  // Authentication routes (no AppShell)
  {
    path: '/auth/signin',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <SignIn />
      </Suspense>
    ),
  },
  {
    path: '/auth/patient/signin',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <PatientSignIn />
      </Suspense>
    ),
  },
  {
    path: '/partner/login',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <StaffSignIn />
      </Suspense>
    ),
  },
  {
    path: '/partner/forgot-password',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <ForgotPassword />
      </Suspense>
    ),
  },
  {
    path: '/auth/reset-password',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <ResetPassword />
      </Suspense>
    ),
  },
  {
    path: '/auth/callback',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <MagicLinkCallback />
      </Suspense>
    ),
  },
  {
    path: '/auth/magic-link-required',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <MagicLinkRequired />
      </Suspense>
    ),
  },
  {
    path: '/auth/error',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <AuthError />
      </Suspense>
    ),
  },
  {
    path: '/auth/setup-password',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <SetupPassword />
      </Suspense>
    ),
  },

  // Authenticated routes that use their own layout (no AppShell)
  {
    path: '/doctor/invitations/accept',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DoctorInvitationAcceptPage />
      </Suspense>
    ),
  },
  {
    path: '/doctor/activate',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DoctorInvitationAcceptPage />
      </Suspense>
    ),
  },
  {
    path: '/ownership-transfer/:token',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <OwnershipTransferAcceptPage />
      </Suspense>
    ),
  },
  {
    path: '/doctor-account-control/approve/:token',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DoctorAccountControlApprovalPage />
      </Suspense>
    ),
  },
  {
    path: '/doctor/onboarding/*',
    element: (
      <RouteGuard requiredRoles={['doctor']}>
        <Suspense fallback={<LoadingSkeleton variant='dashboard' />}>
          <DoctorOnboarding />
        </Suspense>
      </RouteGuard>
    ),
  },
  {
    path: '/doctor/quick-onboarding',
    element: (
      <RouteGuard>
        <Suspense fallback={<LoadingSkeleton variant='dashboard' />}>
          <DoctorQuickOnboardingPage />
        </Suspense>
      </RouteGuard>
    ),
  },
  {
    path: '/doctor/didit-callback',
    element: (
      <RouteGuard>
        <Suspense fallback={<LoadingSkeleton />}>
          <DoctorDiditCallbackPage />
        </Suspense>
      </RouteGuard>
    ),
  },
  {
    path: '/doctors/public/:slug',
    element: <NavigateToDoctorProfile />,
  },
  {
    path: '/doctors/:slug',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DoctorPublicProfilePage />
      </Suspense>
    ),
  },

  // Legacy route redirects (no AppShell needed)
  { path: '/admin/usermanagement', element: <Navigate to='/admin/users' replace /> },
  { path: '/admin/user-management', element: <Navigate to='/admin/users' replace /> },
  { path: '/super-admin/patients', element: <Navigate to='/patients' replace /> },
  { path: '/super-admin/usermanagement', element: <Navigate to='/super-admin/users' replace /> },
  { path: '/super-admin/user-management', element: <Navigate to='/super-admin/users' replace /> },
  { path: '/home', element: <Navigate to='/dashboard' replace /> },
  { path: '/overview', element: <Navigate to='/dashboard' replace /> },

  // Persistent layout route: AppShell renders once and Outlet swaps page content.
  // All authenticated dashboard routes are children so the sidebar/header never
  // remounts during in-app navigation.
  {
    element: (
      <RouteGuard>
        <AppShell />
      </RouteGuard>
    ),
    children: shellChildren,
  },
];

export const router = createBrowserRouter([
  ...localizeRoutes(baseRoutes),
  {
    path: '*',
    element: <RedirectToDefaultLocale />,
  },
]);
